import React, { useEffect, useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  Card,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { AxiosResponse } from "axios";
import http from "../../../utils/http";
import { errorToastMessage } from "../../../utils/toast";
import { DateTime } from "luxon";
import StatusBadge from "../../Common/UI/StatusTag";

interface StudyEnrollment {
  id: string;
  studyName: string;
  status: string;
  createdAt: string;
}

interface ParticipantDetails {
  id: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  gender?: string;
  dob?: string;
  occupation?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    country?: string;
  };
  createdAt: string;
  tags?: Tag[];
}

interface Tag {
  id: string;
  name: string;
  studyId: string;
}

interface Note {
  id: string;
  addedBy: string;
  date: string;
  visibility: string;
  note: string;
  studyId: string;
  editable: boolean;
  studyName: string;
}

interface ParticipantDrawerProps {
  participantId?: string;
  open: boolean;
  onClose: () => void;
  type?: "registry" | "study";
  studyId?: string;
}

// Demographics Section Component
const DemographicsSection: React.FC<{
  participantId: string;
  type?: "registry" | "study";
  studyId?: string;
  onTagsLoaded?: (tags: Tag[]) => void;
  onLoadingChange?: (loading: boolean) => void;
  onErrorChange?: (error: string | null) => void;
}> = ({
  participantId,
  type,
  studyId,
  onTagsLoaded,
  onLoadingChange,
  onErrorChange,
}) => {
  const [data, setData] = useState<ParticipantDetails | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (participantId) {
      const fetchData = async () => {
        try {
          setLoading(true);
          if (onLoadingChange) onLoadingChange(true);
          setError(null);
          if (type === "study") {
            const url = `/participants/${participantId}?studyId=${studyId}`;
            const res: AxiosResponse = await http.get(url);
            const userRes = res?.data?.data;
            const mapped: ParticipantDetails = {
              id: userRes?.id,
              firstName: userRes?.firstName,
              lastName: userRes?.lastName,
              email: userRes?.email,
              phoneNumber: userRes?.phoneNumber,
              gender: userRes?.gender,
              dob: userRes?.dob,
              address: userRes?.address,
              createdAt: userRes?.createdAt,
              tags:
                userRes?.tags?.map((tag: any) => ({
                  id: tag?.tag?.id,
                  name: tag?.tag?.name,
                  studyId: tag?.studyId,
                })) || [],
            };
            setData(mapped);
            if (onTagsLoaded) onTagsLoaded(mapped.tags || []);
            if (onErrorChange) onErrorChange(null);
          } else {
            const url = `/admin/registry/participants/${participantId}`;
            const res: AxiosResponse = await http.get(url);
            const resData = res?.data?.data;
            const mapped: ParticipantDetails = {
              ...resData,
              tags:
                resData?.tags?.map((tag: any) => ({
                  id: tag?.tag?.id,
                  name: tag?.tag?.name,
                  studyId: tag?.studyId,
                })) || [],
            };
            setData(mapped);
            if (onTagsLoaded) onTagsLoaded(mapped.tags || []);
            if (onErrorChange) onErrorChange(null);
          }
        } catch (err) {
          setError("Failed to load participant details");
          errorToastMessage(err as Error);
          // propagate error up for tags section
          if (onTagsLoaded) onTagsLoaded([]);
          if (onErrorChange) onErrorChange("Failed to load tags");
        } finally {
          setLoading(false);
          if (onLoadingChange) onLoadingChange(false);
        }
      };
      fetchData();
    }
  }, [
    participantId,
    type,
    studyId,
    onTagsLoaded,
    onLoadingChange,
    onErrorChange,
  ]);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString();
  };

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: "12px",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        border: "1px solid",
        borderColor: "divider",
        minHeight: loading ? "200px" : "auto",
      }}
    >
      <Typography variant="h2" mb={3}>
        Demographics
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            p: 2,
            bgcolor: "error.lighter",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "error.main",
          }}
        >
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && data && (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 3,
          }}
        >
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              First Name
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.firstName || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Last Name
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.lastName || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Email
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.email || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Phone Number
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.phoneNumber || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Gender
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.gender || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Date of Birth
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {formatDate(data.dob)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Occupation
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.occupation || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Address Line 1
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.address?.line1 || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Address Line 2
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.address?.line2 || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              City
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.address?.city || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              State
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.address?.state || "N/A"}
            </Typography>
          </Box>
          <Box>
            <Typography variant="body2" color="text.secondary" mb={0.5}>
              Country
            </Typography>
            <Typography variant="body2" fontWeight={"regular"}>
              {data.address?.country || "N/A"}
            </Typography>
          </Box>
        </Box>
      )}
    </Card>
  );
};

// Study Enrollments Section Component
const StudyEnrollmentsSection: React.FC<{ participantId: string }> = ({
  participantId,
}) => {
  const [data, setData] = useState<StudyEnrollment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const statusMap: any = {
    live: { label: "Live", variant: "active" },
    not_live: { label: "Not Live", variant: "cancelled" },
    closed: { label: "Closed", variant: "completed" },
  };

  useEffect(() => {
    if (participantId) {
      const fetchDetails = async () => {
        try {
          setLoading(true);
          setError(null);
          const url = `/admin/registry/participants/${participantId}/studies`;
          const res: AxiosResponse = await http.get(url);
          const resData = res.data?.data;
          const newData = resData?.map((row: any) => ({
            id: row?.id,
            studyName: row?.name,
            status: row?.status,
            createdAt:
              row?.addedOn &&
              DateTime.fromISO(row.addedOn).toFormat("dd-MM-yyyy"),
          }));
          setData(newData || []);
        } catch (err) {
          setError("Failed to load study enrollments");
          errorToastMessage(err as Error);
        } finally {
          setLoading(false);
        }
      };
      fetchDetails();
    }
  }, [participantId]);

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: "12px",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        border: "1px solid",
        borderColor: "divider",
        minHeight: loading ? "200px" : "auto",
      }}
    >
      <Typography variant="h2" mb={3}>
        Study Enrollments
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            p: 2,
            bgcolor: "error.lighter",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "error.main",
          }}
        >
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && (
        <>
          {data.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No study enrollments yet
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {data.map((study) => (
                <Box
                  key={study.id}
                  sx={{
                    p: 3,
                    borderRadius: "8px",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "flex-start",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body1" fontWeight={"medium"} mb={1}>
                        {study.studyName}
                      </Typography>
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        <StatusBadge
                          label={
                            statusMap[study?.status]?.label ?? study?.status
                          }
                          variant={statusMap[study?.status]?.variant}
                        />
                      </Box>
                    </Box>
                    <Box sx={{ textAlign: "right" }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Enrolled
                      </Typography>
                      <Typography variant="body2" fontWeight={"regular"}>
                        {study.createdAt}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          )}
        </>
      )}
    </Card>
  );
};

// Tags Section Component (stateless)
const TagsSection: React.FC<{
  tags: Tag[];
  loading?: boolean;
  error?: string | null;
}> = ({ tags, loading, error }) => {
  return (
    <Card
      sx={{
        p: 3,
        borderRadius: "12px",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography variant="h2" mb={3}>
        Tags
      </Typography>
      {error && (
        <Box
          sx={{
            p: 2,
            bgcolor: "error.lighter",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "error.main",
            mb: 2,
          }}
        >
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Box>
      )}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : tags.length > 0 ? (
        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
          {tags.map((tag) => (
            <Chip
              sx={{
                backgroundColor: "#F6F1F8",
                paddingX: "10px",
                paddingY: "2px",
                borderRadius: "6px",
                fontSize: 14,
                fontWeight: 600,
                color: "primary.main",
                m: 0.5,
              }}
              key={tag.id}
              label={tag.name}
            />
          ))}
        </Box>
      ) : (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No tags assigned
          </Typography>
        </Box>
      )}
    </Card>
  );
};

// Notes Section Component
const NotesSection: React.FC<{
  participantId: string;
  type?: "registry" | "study";
  studyId?: string;
}> = ({ participantId, type = "registry", studyId }) => {
  const [notes, setNotes] = useState<Note[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!participantId) return;
    const fetchNotes = async () => {
      try {
        setLoading(true);
        setError(null);
        let res: AxiosResponse;
        if (type === "study") {
          res = await http.get(
            `/participant-notes?studyId=${studyId}&participantId=${participantId}`
          );
        } else {
          res = await http.get(`/participant-notes/${participantId}`);
        }
        const rows = res.data?.data || [];
        const newData = rows.map((item: any) => ({
          id: item.id,
          addedBy: item.createdByUser
            ? `${item.createdByUser.firstName || ""} ${
                item.createdByUser.lastName || ""
              }`.trim()
            : "",
          date: item.createdAt
            ? DateTime.fromISO(item.createdAt).toFormat("dd-MM-yyyy hh:mm a")
            : "",
          visibility: item.isPublic ? "global" : "study",
          note: item.content,
          studyId: item.studyId,
          editable: false,
          studyName: item?.study?.name,
        }));
        setNotes(newData);
      } catch (err) {
        setError("Failed to load notes");
        errorToastMessage(err as Error);
      } finally {
        setLoading(false);
      }
    };
    fetchNotes();
  }, [participantId, type, studyId]);

  return (
    <Card
      sx={{
        p: 3,
        borderRadius: "12px",
        boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
        border: "1px solid",
        borderColor: "divider",
        minHeight: loading ? "200px" : "auto",
      }}
    >
      <Typography variant="h2" mb={3}>
        Notes
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      )}

      {error && (
        <Box
          sx={{
            p: 2,
            bgcolor: "error.lighter",
            borderRadius: "8px",
            border: "1px solid",
            borderColor: "error.main",
          }}
        >
          <Typography variant="body2" color="error.main">
            {error}
          </Typography>
        </Box>
      )}

      {!loading && !error && (
        <>
          {notes.length === 0 ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography variant="body2" color="text.secondary">
                No notes recorded
              </Typography>
            </Box>
          ) : (
            <Stack spacing={2}>
              {notes.map((note) => (
                <Box
                  key={note.id}
                  sx={{
                    p: 3,
                    borderRadius: "8px",
                    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                    border: "1px solid",
                    borderColor: "divider",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Box sx={{ flex: 1 }}>
                      <Typography
                        variant="body2"
                        fontWeight={"medium"}
                        color="text.primary"
                      >
                        {note.addedBy || "—"}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {note.date}
                      </Typography>
                    </Box>
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                      <Chip
                        label={note.visibility}
                        size="small"
                        sx={{
                          textTransform: "capitalize",
                          fontSize: "0.75rem",
                          height: "20px",
                        }}
                      />
                      {note.studyName && (
                        <Chip
                          label={note.studyName}
                          size="small"
                          variant="outlined"
                          sx={{
                            fontSize: "0.75rem",
                            height: "20px",
                            maxWidth: "150px",
                            "& .MuiChip-label": {
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Typography
                    variant="body2"
                    color="text.primary"
                    sx={{ lineHeight: 1.6 }}
                  >
                    {note.note || "—"}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </>
      )}
    </Card>
  );
};

const ParticipantDrawer: React.FC<ParticipantDrawerProps> = ({
  participantId,
  open,
  onClose,
  type,
  studyId,
}) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [demographicsLoading, setDemographicsLoading] = useState(false);
  const [tagsError, setTagsError] = useState<string | null>(null);
  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      data-testid="drawer-participant-details"
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "600px", md: "800px" },
          maxWidth: "100vw",
        },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            p: 3,
            borderBottom: "1px solid",
            borderColor: "divider",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={"medium"}>
              Participant Profile
            </Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              View demographic information, study enrollment history, tags, and
              notes
            </Typography>
          </Box>
          <IconButton onClick={onClose} data-testid="button-close-drawer">
            <Close />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
          {participantId && (
            <Stack spacing={4}>
              {/* Demographics Section */}
              <DemographicsSection
                participantId={participantId}
                type={type}
                studyId={studyId}
                onTagsLoaded={setTags}
                onLoadingChange={setDemographicsLoading}
                onErrorChange={setTagsError}
              />

              {/* Study Enrollments Section */}
              {type === "registry" && (
                <StudyEnrollmentsSection participantId={participantId} />
              )}

              {/* Tags Section */}
              <TagsSection
                tags={tags}
                loading={demographicsLoading}
                error={tagsError}
              />

              {/* Notes Section */}
              <NotesSection
                participantId={participantId}
                type={type}
                studyId={studyId}
              />

              {/* Recent Responses Section - Commented out for now */}
              {/* 
              <Box>
                <Typography variant="h2"  mb={2}>
                  Recent Responses (Last 6 Months)
                </Typography>
                <Card sx={{ p: 4, borderRadius: "12px", textAlign: "center" }}>
                  <Typography variant="body2" color="text.secondary">
                    Recent responses section temporarily disabled
                  </Typography>
                </Card>
              </Box>
              */}
            </Stack>
          )}
        </Box>
      </Box>
    </Drawer>
  );
};

export default ParticipantDrawer;
